import React from 'react'

import { Stack, Typography } from '@mui/material'

import { Avatar } from '@repo/ui'
import { Preview } from 'components'
// Sibling path, not the `components` barrel: this file is exported FROM that barrel,
// so routing through it would be a cycle. Same reason `AnnouncementKindBadge` does it.
import { StatusChip } from '../../StatusChip/StatusChip'

/**
 * A recipient identified only by a typed email address — someone who may have no
 * Vera account at all.
 *
 * WHY THIS EXISTS AS ITS OWN COMPONENT rather than reusing `OwnerPreview` with a
 * synthetic customer: it would have rendered identically to a real owner, and the
 * whole point of this row is that it is NOT one. The sender needs to see that
 * distinction at the two moments it matters — when choosing the row, and again on
 * the selected card immediately before pressing Transfer — so the difference has to
 * be visible, not merely true.
 *
 * Structurally it is `OwnerPreview` (same `Preview` wrapper, same avatar + primary +
 * secondary shape, same `size` contract), so the list row it renders in lines up
 * with the owner rows above it and the selected card matches the owner card it
 * replaces. Only the copy differs, which is the only thing that should.
 */
interface InvitePreviewProps {
  email: string
  onChange?: any
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

/**
 * The consequence, stated plainly (working-conventions §15: separate sentences, not
 * em-dash-joined clauses).
 *
 * Names the ACTION rather than asserting a state. The recipient does not become an
 * owner by being sent this — the certificate moves to `waiting_accept` and stays
 * there until they claim it from the emailed link — so "invite as new owner" is
 * exact where "make them the owner" would not be.
 *
 * It is also the only claim this component can support. The picker filters a bounded,
 * company-scoped page of customers, so "this person has no Vera account" is not
 * something it is in a position to know: the address may well belong to an existing
 * user of another brand, or to a Vera admin. What IS always true is that an invite
 * goes to this address and that whoever accepts becomes the certificate's new owner.
 */
const INVITE_SUBLABEL = 'Send to this email. Invite as new owner'

/**
 * The badge's one entry, in `StatusChip`'s shape.
 *
 * Reusing the platform's canonical pill rather than hand-rolling a chip (§28/§47):
 * `@repo/ui`'s own `Chip` is a shell whose component is commented out and which the
 * barrel does not export, so it is one of the layer-1 names that resolves in
 * TypeScript and renders nothing — checked before importing, per §47.
 *
 * `brand[600]` because this is not a lifecycle state and must not read as one. The
 * row sits in a list beside real owners; the badge's job is to mark WHICH KIND of
 * recipient this row is, not to report a status the recipient is in.
 *
 * Tone is derived by `statusTone`, so it follows light/dark with everything else.
 *
 * The label is "Invite", NOT "New owner". It briefly said the latter, and that was a
 * claim this component is not in a position to make: the picker is company-scoped, so an
 * address matching nobody in THIS brand's customer list may still belong to an existing
 * owner elsewhere — `spencer@vera.inc` is a live counterexample. "New owner" survives
 * only on the reading "new owner of this certificate", which is true of every recipient
 * and therefore says nothing. It also repeated the sublabel, which already ends "invite
 * as new owner", so the row asserted the same thing twice.
 *
 * "Invite" is verifiable — an invite is exactly what sending to this row does — and it
 * names the row's KIND, which is the badge's job, while the sublabel names the action.
 */
const INVITE_BADGE = {
  invite: { label: 'Invite', color: '#7F48DF' },
}

export const InvitePreview = (props: InvitePreviewProps) => {
  const { email, onChange = false, disabled = false, size = 'large' } = props
  if (!email) return null

  return (
    <Preview size={size} item={email} onChange={onChange} disabled={disabled}>
      <Stack direction="row" alignItems="center" spacing={size == 'small' ? 2 : 4}>
        {/* `Avatar` already derives initials from an address (`getInitials` splits on
            the local part), so the invite row gets the same treatment as every other
            person in the list instead of a placeholder glyph. */}
        <Avatar
          name={email}
          sx={{
            width: size == 'large' ? 64 : size == 'medium' ? 48 : 28,
            height: size == 'large' ? 64 : size == 'medium' ? 48 : 28,
          }}
        />
        <Stack spacing={2} sx={{ minWidth: 0, maxWidth: size == 'small' ? 100 : '100%' }}>
          {/* Badge sits ON the address line, not below it: the sublabel is the
              explanation and the badge is the label, so stacking them would read as
              two explanations. `minWidth: 0` on the email keeps it the element that
              truncates — the badge is short and fixed, so shrinking it first would
              cost more legibility than it saves. */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {email}
            </Typography>
            {size != 'small' && (
              <StatusChip statuses={INVITE_BADGE} status="invite" size="small" />
            )}
          </Stack>
          {size != 'small' && (
            // lineHeight 1.5 — descriptive copy, not a data field (§15).
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {INVITE_SUBLABEL}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Preview>
  )
}
